// Saludo de bienvenida del Búho — restaura la experiencia de onboarding.
// Aparece una vez por sesión cuando el usuario entra a la app, con un mensaje
// según su rol. Para estudiantes (con voz activa) el búho saluda en voz alta.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { FloatingOwl } from "@/components/OwlMascot";
import { getAgeTheme } from "@/lib/quizThemes";
import { speakOwl, isOwlVoiceOn, defaultOwlVoiceForStudents } from "@/lib/owlVoice";

const greetingFor = (user) => {
  const first = user?.name?.split(" ")[0] || "";
  switch (user?.role) {
    case "admin":
      return `¡Hola ${first}! Soy Zarek 🦉. Pulsa el botón 🦉 arriba para ver tutoriales paso a paso de cada módulo.`;
    case "franquiciado":
      return `¡Bienvenid@ ${first}! Gestiona tu franquicia desde aquí. Pulsa 🦉 si quieres un tutorial guiado.`;
    case "docente":
      return `¡Hola profe ${first}! Acompaña a tus estudiantes y abre el 🦉 para ver cómo asignar diagnósticos o iniciar clases.`;
    case "estudiante":
      return `¡Hola ${first}! Soy Zarek 🦉, tu búho guía. ¡Vamos a aprender juntos! Pulsa el botón de Zarek para que te muestre todo.`;
    default:
      return `¡Hola ${first}! Soy Zarek 🦉. Pulsa el botón 🦉 arriba si necesitas ayuda.`;
  }
};

export default function WelcomeOwl() {
  const { user } = useAuth();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const inRoom = /\/clases-en-vivo\/[^/]+\/sala/.test(location.pathname);

  useEffect(() => {
    if (!user?.id) return;
    // v5.6.0 · NO saluda dentro de una sala de clase en vivo y recuerda el saludo por DÍA (no solo por pestaña):
    // al abrir la sala en otra pestaña/ventana ya no vuelve a salir el búho.
    if (inRoom) { setShow(false); return; }
    const key = `jasq_welcomed_${user.id}`;
    const today = new Date().toISOString().slice(0, 10);
    try { if (sessionStorage.getItem(key) === "1" || localStorage.getItem(key) === today) return; } catch (_) {}

    const msg = greetingFor(user);
    const t = setTimeout(() => {
      // Marcamos como visto DENTRO del callback (no antes): así StrictMode,
      // que monta/desmonta dos veces, no nos deja sin saludo.
      try {
        if (sessionStorage.getItem(key) === "1" || localStorage.getItem(key) === today) return;
        sessionStorage.setItem(key, "1");
        localStorage.setItem(key, today);
      } catch (_) {}
      setShow(true);
      if (user.role === "estudiante") {
        defaultOwlVoiceForStudents();
        const theme = getAgeTheme(user.age);
        // Solo lee en voz alta a los más pequeños si la voz está activa.
        if (isOwlVoiceOn() && (theme.id === "kids" || theme.id === "teen")) {
          speakOwl(msg);
        }
      }
    }, 900);
    return () => clearTimeout(t);
  }, [user?.id, inRoom]);

  if (!show || !user) return null;
  const variant = user.role === "estudiante" ? "party" : "hint";
  return (
    <FloatingOwl
      message={greetingFor(user)}
      variant={variant}
      duration={5000}
      onDone={() => setShow(false)}
    />
  );
}
